import { useAuth } from '@neotech-solutions-org/neofusion-fe-shared';
import { useCallback, useMemo } from 'react';
import { BaseTab, Permission } from '../@types';
import { PERMISSIONS, USER_ROLES } from '../constants/keycloak';
import { UserRole } from '../@types/api';

const usePermissions = () => {
  const user = useAuth();

  const hasPermission = useCallback(
    (permission: Permission) => {
      return user.permissions.includes(PERMISSIONS[permission]);
    },
    [user]
  );

  const role: UserRole = useMemo(() => {
    if (hasPermission('defaultSuperAdminRole')) return USER_ROLES.SUPER_ADMIN;
    if (hasPermission('defaultAdminRole')) return USER_ROLES.ADMIN;
    if (hasPermission('defaultManagerRole')) return USER_ROLES.MANAGER;
    if (hasPermission('defaultAgentRole')) return USER_ROLES.AGENT;
    if (hasPermission('defaultAffiliateRole')) return USER_ROLES.AFFILIATE;
    if (hasPermission('defaultTraderRole')) return USER_ROLES.TRADER;
    return USER_ROLES.TECH_SUPPORT;
  }, [hasPermission]);

  const isSuperAdmin = useMemo(() => role === USER_ROLES.SUPER_ADMIN, [role]);
  const isAdmin = useMemo(() => role === USER_ROLES.ADMIN, [role]);
  const isManager = useMemo(() => role === USER_ROLES.MANAGER, [role]);
  const isAgent = useMemo(() => role === USER_ROLES.AGENT, [role]);
  const isAffiliate = useMemo(() => role === USER_ROLES.AFFILIATE, [role]);
  const isTrader = useMemo(() => role === USER_ROLES.TRADER, [role]);
  const isTechSupport = useMemo(() => role === USER_ROLES.TECH_SUPPORT, [role]);

  const filterAccessibleTabs = useCallback(
    <T extends BaseTab>(tabs: T[]) => {
      return tabs.filter((tab) => {
        // If tab.permission is undefined, we allow access to all users
        if (!tab.permission) return true;

        return hasPermission(tab.permission);
      });
    },
    [hasPermission]
  );

  return {
    hasPermission,
    role,
    filterAccessibleTabs,
    isSuperAdmin,
    isAdmin,
    isManager,
    isAgent,
    isAffiliate,
    isTrader,
    isTechSupport,
  };
};

export default usePermissions;
